import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { MessageCircle, Plus, RefreshCw, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useSiteSettings, openLiveChatWidget } from "../../contexts/SiteSettingsContext";
import { Logo } from "../Logo";
import { BdFlag } from "../BdFlag";
import { NotificationBell } from "./NotificationBell";

// Desktop-only inline nav. Hidden on mobile/tablet so the existing
// MobileBottomBar / SideMenu continue to handle small screens unchanged.
const DESKTOP_NAV = [
  { to: "/sports", label: "Sports" },
  { to: "/casino", label: "Casino" },
  { to: "/slots", label: "Slots" },
];

const DesktopNavTabs = () => (
  <nav
    className="hidden lg:flex items-center gap-1 ml-4 xl:ml-6"
    aria-label="primary"
    data-testid="desktop-nav"
  >
    {DESKTOP_NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={`desktop-nav-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          [
            "px-3 xl:px-4 h-9 inline-flex items-center rounded-md text-sm font-semibold tracking-wide transition-colors whitespace-nowrap",
            isActive
              ? "text-white bg-white/10"
              : "text-white/70 hover:text-white hover:bg-white/5",
          ].join(" ")
        }
      >
        {item.label}
      </NavLink>
    ))}
  </nav>
);

export const SiteHeader = () => {
  const { user, checking, refreshGameWallet } = useAuth();
  const { liveChat } = useSiteSettings();
  const [refreshingMain, setRefreshingMain] = useState(false);
  const [refreshingGame, setRefreshingGame] = useState(false);
  const navigate = useNavigate();

  const handleLiveChat = () => {
    // 1) Prefer a configured 3rd-party widget (Tawk/Crisp/Tidio/Intercom/Zendesk/Jivo)
    const opened = openLiveChatWidget();
    if (opened) return;
    // 2) Fallback to our in-app Max_Ai LiveChatPanel — works without any external
    //    integration so it functions identically on preview and on VPS.
    try {
      window.dispatchEvent(new CustomEvent("open-live-chat-panel"));
    } catch {
      toast.message("Live chat is loading… please try again in a moment.");
    }
  };

  // Display balance — live from server via useAuth context.
  // Defensive clamp: a negative site balance is never user-visible —
  // it can only occur from operator error or a partial-failure race
  // during a WCKT (transfer-wallet) round-trip. In that case showing
  // "৳ -500" terrifies players unnecessarily; we display "৳ 0" instead
  // and rely on the admin negative-balance scanner to alert ops.
  const rawBalance = Number(user?.balance ?? 0);
  const balance = Number.isFinite(rawBalance) && rawBalance > 0 ? rawBalance : 0;
  const currency = user?.currency || "BDT";
  const currencySymbol = currency === "BDT" ? "৳" : "$";

  // ── BOTH refresh buttons trigger the FULL 9-Wickets sync + cashout ──
  // Per product decision: the 9-Wickets cashout flow must be present in
  // BOTH refresh buttons on the site — it cannot be split into a "local
  // only" button. So both handlers below call refreshGameWallet() which
  // internally:
  //   1. Hits the per-user atomic mutex (no concurrent cashouts)
  //   2. Probes DST balance (only credits on DST code=0 — source of truth)
  //   3. Pulls any 9-Wickets balance back to the site wallet
  //   4. Re-reads the site balance
  // Each click generates a fresh crypto-random Idempotency-Key, so even
  // rapid double-clicks NEVER cause duplicate processing — the second call
  // with the same key returns the cached result of the first.
  const onRefreshMainWallet = async () => {
    if (refreshingMain) return;
    setRefreshingMain(true);
    try {
      await refreshGameWallet();
    } catch {
      toast.error("Couldn't refresh balance");
    } finally {
      setRefreshingMain(false);
    }
  };

  const onRefreshGameWallet = async () => {
    if (refreshingGame) return;
    setRefreshingGame(true);
    try {
      await refreshGameWallet();
    } catch {
      toast.error("Couldn't refresh balance");
    } finally {
      setRefreshingGame(false);
    }
  };

  const onDeposit = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    navigate("/profile/deposit");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur"
      style={{
        background: "rgba(10,17,25,0.95)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      {/* Main row — slim and clean like baji */}
      <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-5 lg:px-8 h-14 lg:h-16 max-w-7xl lg:max-w-[1600px] mx-auto min-w-0">
        <Link to="/" className="flex items-center gap-2 lg:gap-3 shrink-0 min-w-0" data-testid="header-logo-link">
          <Logo className="text-[22px] sm:text-3xl lg:text-[32px] font-extrabold tracking-tight" />
        </Link>

        {/* Desktop-only inline navigation */}
        <DesktopNavTabs />

        {user ? (
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2 lg:gap-2.5 min-w-0">
            {/* Balance pill — taka symbol | amount | refresh | green + */}
            <div
              data-testid="balance-pill"
              className="flex items-stretch h-9 lg:h-10 rounded-md overflow-hidden bg-[#1a2030] border border-white/5 shrink-0"
            >
              <div className="flex items-center gap-1.5 pl-2 pr-1.5 lg:pl-3 lg:pr-2">
                <span
                  aria-hidden
                  className="h-5 w-5 rounded-full flex items-center justify-center text-[11px] font-extrabold text-white shrink-0"
                  style={{ background: "linear-gradient(135deg,#19a55c 0%,#0f7a40 100%)" }}
                >
                  {currencySymbol}
                </span>
                <span
                  data-testid="balance-amount"
                  className="text-sm lg:text-[15px] font-bold text-white tabular-nums"
                >
                  {balance.toLocaleString()}
                </span>
              </div>
              <button
                type="button"
                onClick={onRefreshMainWallet}
                aria-label="refresh balance"
                title="Refresh balance"
                data-testid="main-wallet-refresh"
                className="px-2 hover:bg-white/5 flex items-center text-white/70 disabled:opacity-50"
                disabled={refreshingMain || refreshingGame}
              >
                <RefreshCw size={13} className={(refreshingMain || refreshingGame) ? "animate-spin" : ""} />
              </button>
              {/* Mobile/tablet only quick + button — desktop uses dedicated Deposit text button below */}
              <button
                type="button"
                onClick={onDeposit}
                aria-label="quick deposit"
                data-testid="balance-plus"
                className="lg:hidden px-2 bg-[#19a55c] hover:bg-[#21c46e] active:bg-[#168f4f] flex items-center text-white transition-colors"
              >
                <Plus size={16} strokeWidth={3} />
              </button>
            </div>

            {/* Desktop only — Withdrawal + Deposit text buttons */}
            <Link
              to="/profile/withdraw"
              data-testid="header-withdraw-btn"
              className="hidden lg:inline-flex items-center justify-center h-10 px-4 rounded-md text-white text-sm font-bold tracking-wide bg-[#262e3f] hover:bg-[#2f3850] active:bg-[#1f2636] border border-white/10 transition-colors whitespace-nowrap"
            >
              Withdrawal
            </Link>
            <button
              type="button"
              onClick={onDeposit}
              data-testid="header-deposit-btn"
              className="hidden lg:inline-flex items-center justify-center h-10 px-4 rounded-md text-white text-sm font-extrabold tracking-wide active:scale-[0.98] transition-all whitespace-nowrap"
              style={{
                background:
                  "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                boxShadow:
                  "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              Deposit
            </button>

            {/* Notification bell — visible on both mobile + desktop, shows unread badge */}
            <NotificationBell />

            {/* Mobile/tablet flag — REMOVED per admin request */}

            {/* Desktop only — profile avatar (links to dashboard) */}
            <Link
              to="/dashboard"
              data-testid="header-avatar"
              aria-label="account"
              className="hidden lg:flex h-10 w-10 shrink-0 rounded-full items-center justify-center text-white font-display font-bold text-base ring-2 ring-white/15 hover:ring-emerald-400/60 transition-all"
              style={{
                background:
                  "radial-gradient(120% 120% at 30% 30%, #ff5466 0%, #b8132a 70%)",
                boxShadow: "0 4px 12px rgba(184,19,42,0.45)",
              }}
            >
              {(user?.username || user?.email || "U").charAt(0).toUpperCase()}
            </Link>
          </div>
        ) : checking ? (
          /* Auth state is being verified — render a placeholder of the same width
             so the header doesn't briefly flash Login/Signup buttons for a user
             who actually has a valid session. */
          <div
            data-testid="auth-checking-placeholder"
            aria-hidden
            className="ml-auto flex items-center gap-1.5 sm:gap-2.5 min-w-0"
          >
            <div className="h-9 w-[88px] sm:w-[96px] rounded-md bg-white/[0.04] border border-white/5 animate-pulse" />
            <div className="h-9 w-[88px] sm:w-[96px] rounded-md bg-white/[0.04] border border-white/5 animate-pulse" />
          </div>
        ) : (
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2.5 min-w-0">
            <Link
              to="/login"
              data-testid="header-login-btn"
              className="flex items-center justify-center px-4 sm:px-5 h-9 rounded-md text-white/90 text-sm font-bold tracking-wide border border-white/15 bg-white/[0.03] hover:bg-white/10 hover:border-white/30 hover:text-white active:scale-[0.98] transition-all whitespace-nowrap"
            >
              Log in
            </Link>
            <Link
              to="/register"
              data-testid="header-signup-btn"
              className="flex items-center justify-center px-4 sm:px-5 h-9 rounded-md text-white text-sm font-extrabold tracking-wide active:scale-[0.98] transition-all whitespace-nowrap"
              style={{
                background:
                  "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                boxShadow:
                  "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              Sign up
            </Link>

            <span className="hidden lg:inline-block h-5 w-px bg-white/10 mx-1" aria-hidden />

            <button
              type="button"
              onClick={handleLiveChat}
              data-testid="live-chat-btn"
              style={{ display: liveChat?.show_header_button === false ? "none" : undefined }}
              className="hidden lg:flex items-center gap-2 px-3 h-9 rounded-md text-white/75 hover:text-white hover:bg-white/5 text-sm shrink-0 transition-colors"
            >
              <MessageCircle size={16} className="text-[#19a55c]" />
              <span className="hidden xl:inline font-medium">24/7 Live Chat</span>
            </button>
            <button
              type="button"
              aria-label="language"
              data-testid="lang-switcher"
              className="hidden lg:flex items-center justify-center h-9 w-9 shrink-0 rounded-full overflow-hidden ring-1 ring-white/10 hover:ring-white/30 transition-all"
            >
              <BdFlag size={24} />
            </button>
          </div>
        )}
      </div>
    </header>
  );
};
